/* =============================================================
   Teaching Summary, as a chart.

   What it replaces: six CSS-drawn bars sharing one track. Four of
   those figures were percentages and two were counts, and the
   counts were scaled as `count / maxCount * 100` to fit beside
   them -- so "12 sessions marked" drew a full-width bar next to
   "85% attendance" and read as the better number. A bar length
   has to mean the same thing on every row or it means nothing.

   So: the four rates go on a real 0-100 axis, and the two counts
   come out of the chart and sit under it as figures. Nothing is
   dropped -- every value the old list showed is still here, and
   each one is now shown on a scale it belongs to.

   Colour: ONE hue, the palette's chart accent (see utils/chartTheme).
   The four bars are one series -- "a rate" -- with a label on each,
   and the label already says which rate it is. They used to wear
   four hues, which spent the colour channel re-saying the label and
   read as four unrelated measures. Chrome follows the palette too.
   ============================================================= */

import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useChartTheme } from '../../../utils/chartTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

/* chart.js ships no value labels, and this panel's whole point was
   that you could read the number off the bar. Local to this chart --
   passed on the <Bar> rather than registered globally -- so the other
   charts in the app are untouched.

   It is a module-level object, so it cannot close over the theme: the
   colours ride on the dataset instead (labelOnFill / labelOffFill),
   which is what makes a palette change repaint the labels along with
   the bars. */
const valueLabels = {
  id: 'tdsValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0] || {};
    const meta = chart.getDatasetMeta(0);
    const texts = dataset.valueLabels || [];

    ctx.save();
    ctx.font = `700 12.5px ${ChartJS.defaults.font.family}`;
    ctx.textBaseline = 'middle';

    meta.data.forEach((bar, i) => {
      const text = texts[i];
      if (!text) return;
      // Inside the bar when it is long enough to hold the text; otherwise
      // just past its end, in ink. A short bar with its label hanging
      // outside beats a label clipped to a sliver.
      const length = Math.abs(bar.x - bar.base);
      const inside = length > ctx.measureText(text).width + 20;
      ctx.fillStyle = inside ? dataset.labelOnFill : dataset.labelOffFill;
      ctx.textAlign = inside ? 'right' : 'left';
      ctx.fillText(text, inside ? bar.x - 10 : bar.x + 8, bar.y);
    });

    ctx.restore();
  },
};

export default function TeachingSummaryChart({ insights }) {
  const theme = useChartTheme();

  const { rates, figures } = useMemo(() => {
    const perf = insights?.performance || {};
    const att = insights?.attendance || {};
    const last30 = att.last30Days || {};
    const mocks = insights?.mockInterviews || {};
    const students = insights?.students || {};

    /* A rate is only plotted when there is something behind it. An
       "Interview success 0%" bar for a trainer who has evaluated
       nobody is a false reading, not a low score -- it drops to the
       figures below instead, saying why. */
    const all = [
      {
        key: 'attendance',
        label: 'Student attendance',
        value: perf.avgStudentAttendance,
        note: `across ${students.rated || 0} student${students.rated === 1 ? '' : 's'} with sessions held`,
        has: students.rated > 0,
      },
      {
        key: 'last30',
        label: 'Last 30 days',
        value: last30.rate,
        note: `${last30.sessionsConducted || 0} session${last30.sessionsConducted === 1 ? '' : 's'} in the last month`,
        has: (last30.sessionsConducted || 0) > 0,
      },
      {
        key: 'timeline',
        label: 'Timeline progress',
        value: perf.avgTimelineProgress,
        note: 'average across running batches',
        has: perf.avgTimelineProgress != null,
      },
      {
        key: 'mockSuccess',
        label: 'Interview success',
        value: mocks.successRate,
        note:
          `${mocks.qualified || 0} qualified of ${mocks.evaluated || 0}` +
          (mocks.averageScore == null ? '' : ` · ${mocks.averageScore}% avg. score`),
        has: mocks.successRate != null && (mocks.evaluated || 0) > 0,
      },
    ];

    const figs = [
      {
        key: 'sessions',
        label: 'Sessions marked',
        value: att.sessionsConducted ?? 0,
        note: `${att.presentMarks || 0} present of ${att.expectedMarks || 0} expected`,
      },
      {
        key: 'mocks',
        label: 'Mock interviews',
        value: mocks.assigned ?? 0,
        note: mocks.assigned
          ? `${mocks.attended || 0} of ${mocks.invited || 0} students attended`
          : 'None assigned to you',
      },
      // The rates with nothing behind them, said in words rather than
      // drawn as a zero.
      ...all
        .filter((r) => !r.has)
        .map((r) => ({ key: r.key, label: r.label, value: '—', note: r.note })),
    ];

    return { rates: all.filter((r) => r.has), figures: figs };
  }, [insights]);

  const data = useMemo(() => {
    const fill = theme.series[0];
    return {
      labels: rates.map((r) => r.label),
      datasets: [
        {
          label: 'Rate',
          data: rates.map((r) => Math.max(0, Math.min(100, Number(r.value) || 0))),
          valueLabels: rates.map((r) => `${Math.round(Number(r.value) || 0)}%`),
          labelOnFill: theme.onFill(fill),
          labelOffFill: theme.ink.body,
          backgroundColor: fill,
          hoverBackgroundColor: fill,
          // Rounded at the value, square at the baseline it grows from.
          borderRadius: 4,
          borderSkipped: 'start',
          barThickness: 20,
        },
      ],
    };
  }, [rates, theme]);

  const options = useMemo(
    () => ({
      // Horizontal: the labels are phrases, and rotating them under a
      // vertical axis makes them unreadable.
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 44 } },
      animation: { duration: 550 },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) => [` ${ctx.parsed.x}%`, ` ${rates[ctx.dataIndex]?.note || ''}`],
          },
        },
      },
      scales: {
        x: theme.valueAxis({
          min: 0,
          max: 100,
          ticks: { stepSize: 25, callback: (v) => `${v}%` },
        }),
        y: theme.categoryAxis({
          ticks: { font: { size: 12.5, weight: '600' } },
        }),
      },
    }),
    [rates, theme],
  );

  return (
    <>
      {rates.length === 0 ? (
        <p className="td-empty">
          Nothing to chart yet — no sessions marked and no interviews evaluated.
        </p>
      ) : (
        <div
          className="td-bars-canvas"
          style={{ height: `${Math.max(140, rates.length * 38 + 46)}px` }}
        >
          <Bar data={data} options={options} plugins={[valueLabels]} />
        </div>
      )}

      {/* Counts, not rates. They were in the chart and drawn against
          the largest count, which made the scale mean two things. */}
      <dl className="td-bars-figs">
        {figures.map((f) => (
          <div className="td-bars-fig" key={f.key}>
            <dt className="td-bars-fig-label">{f.label}</dt>
            <dd className="td-bars-fig-value">{f.value}</dd>
            <dd className="td-bars-fig-note">{f.note}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}
